/**
 * auth.js — client for the BisaWofa email-OTP login.
 *
 * Talks to the PHP endpoints vendored under public/api/auth/ (same-origin, so
 * the PHP session cookie is sent automatically; `credentials: 'include'` is set
 * explicitly anyway so it also works if the endpoints are ever moved behind a
 * configured CORS origin).
 *
 * Flow:
 *   checkSession()      → is there an active session?
 *   requestOtp(email)   → server emails a 6-digit code
 *   verifyOtp(code)     → establishes the session
 *   logout()            → destroys it
 *
 * All calls resolve to a normalised { ok, msg, email? } — never throw for a
 * server-side "no" — and only reject/return ok:false for genuine failures.
 *
 * @module auth
 */

const BASE = `${import.meta.env.BASE_URL}api/auth/`;

/** The signed-in user's email, or null. Populated by checkSession/verifyOtp. */
let currentEmail = null;

/**
 * The signed-in user's profile row from bw_social.users, or null.
 * Shape: { id, username, email, display_name, bio, language, country_code,
 *          home_latitude, home_longitude, home_pluscode, avatar_url,
 *          is_active, email_verified }
 * May be null even when signed in, if the DB was briefly unreachable.
 */
let currentUser = null;

/**
 * The signed-in user's ActivityPub actor, or null.
 * Provisioned server-side at first sign-in (Phase 1 of the concept paper).
 * Shape: { id, uri, preferred_username }. Null if the social API was
 * unreachable — the server retries on the next sign-in.
 */
let currentActor = null;

/** @returns {string|null} the signed-in email (sync, cached). */
export function getCurrentEmail() { return currentEmail; }
/** @returns {object|null} the signed-in user's profile (sync, cached). */
export function getCurrentUser() { return currentUser; }
/** @returns {object|null} the signed-in user's ActivityPub actor (sync, cached). */
export function getCurrentActor() { return currentActor; }
/** @returns {boolean} whether a session was established this page-load. */
export function isSignedIn() { return !!currentEmail; }

/**
 * A short human name for the signed-in user, for greetings and attribution.
 * @returns {string|null}
 */
export function getDisplayName() {
  if (!currentUser) return currentEmail;
  return currentUser.display_name || currentUser.username || currentUser.email || null;
}

async function post(endpoint, params) {
  const res = await fetch(BASE + endpoint, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: params ? new URLSearchParams(params).toString() : '',
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

/**
 * Is there an active login session?
 * @returns {Promise<{ ok:boolean, email:string|null, offline?:boolean }>}
 */
export async function checkSession() {
  try {
    const r = await post('check_session.php');
    const ok = r?.code === 1 && !!r.email;
    currentEmail = ok ? r.email : null;
    currentUser  = ok ? (r.user || null) : null;
    return { ok, email: currentEmail, user: currentUser };
  } catch (err) {
    // Network/server unreachable — distinct from "not signed in" so the caller
    // can avoid hard-gating a user who is simply offline.
    console.warn('[auth] session check failed:', err.message);
    currentEmail = null;
    currentUser  = null;
    return { ok: false, email: null, user: null, offline: true };
  }
}

/**
 * Ask the server to email a one-time code.
 * @param {string} email
 * @returns {Promise<{ ok:boolean, msg:string }>}
 */
export async function requestOtp(email) {
  try {
    const r = await post('login.php', { email });
    return { ok: r?.code === 1, msg: r?.msg || (r?.code === 1 ? 'Code sent.' : 'Could not send the code.') };
  } catch (err) {
    return { ok: false, msg: 'Server unreachable. Check your connection.' };
  }
}

/**
 * Verify the code and establish the session.
 *
 * NOTE: the upstream endpoint returned code 1 for an EXPIRED code as well as a
 * verified one. Our vendored copy fixes that, but we defensively require an
 * `email` in the response (only sent on genuine success) so an old/unpatched
 * backend can never be mistaken for a successful login.
 *
 * @param {string} otp
 * @returns {Promise<{ ok:boolean, msg:string, email:string|null }>}
 */
export async function verifyOtp(otp) {
  try {
    const r = await post('process_otp.php', { otp });
    const ok = r?.code === 1 && !!r.email;
    currentEmail = ok ? r.email : null;
    currentUser  = ok ? (r.user || null) : null;
    currentActor = ok ? (r.actor || null) : null;
    return {
      ok,
      msg: r?.msg || (ok ? 'Signed in.' : 'That code is not correct.'),
      email: currentEmail,
      user: currentUser,
      actor: currentActor,
    };
  } catch (err) {
    return { ok: false, msg: 'Server unreachable. Check your connection.', email: null, user: null };
  }
}

/** End the session. */
export async function logout() {
  try { await post('logout.php'); } catch (_) {}
  currentEmail = null;
  currentUser  = null;
  currentActor = null;
}

/**
 * Update the signed-in user's own profile in bw_social.users.
 * The server takes the user id from the session, never from here.
 *
 * @param {object} fields any of display_name, bio, language, country_code,
 *                        home_latitude, home_longitude, home_pluscode,
 *                        avatar_url, phone
 * @returns {Promise<{ ok:boolean, user:object|null, msg?:string }>}
 */
export async function updateProfile(fields) {
  try {
    const res = await fetch(`${import.meta.env.BASE_URL}api/users/update.php`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(fields || {}),
    });
    const r = await res.json();
    if (r?.ok && r.user) {
      currentUser  = r.user;
      currentEmail = r.user.email;
      return { ok: true, user: currentUser };
    }
    return { ok: false, user: null, msg: r?.error || 'Could not save your profile.' };
  } catch (err) {
    return { ok: false, user: null, msg: 'Server unreachable. Check your connection.' };
  }
}

export default {
  checkSession, requestOtp, verifyOtp, logout, updateProfile,
  getCurrentEmail, getCurrentUser, getCurrentActor, getDisplayName, isSignedIn,
};
